import React from 'react';
import { Text, TextAs, TextSizes, Tooltip } from '@clockout-dev/design-elements';
import { getIssueStatus } from './issueStatus.constants';
import styles from '../styles/status-pill-tag.module.scss';

/**
 * Pill rendering an ISSUE-TRACKER status bucket — the Jira issues table, the
 * issue side panel and the linked-issue card on a support ticket all use it, so
 * one status reads identically everywhere.
 *
 * Provider-agnostic on purpose (see `issueStatus.constants`): nothing here
 * mentions Jira, so a second mirrored tracker reuses it.
 *
 * Kept separate from `support/elements/SupportStatusPill` deliberately. That one
 * is the FIVE support-ticket statuses, maps status → icon/label/tone through its
 * own tables and has no tone prop; folding a second vocabulary into it would
 * make one lookup answer for two unrelated enums. The shared
 * `status-pill-tag.module.scss` is what exists to avoid that.
 *
 * The visible word comes from the SERVED map when the caller has it, because the
 * FI-facing prose is the server's to change (`label`); only the tone is decided
 * here. An unrecognised key renders neutral with the raw value in the tooltip —
 * never dropped, never shown as another status.
 *
 * @param {Object} props
 * @param {string} [props.status] the status bucket (`queued`, `in_progress`, …)
 * @param {{ label?: string }} [props.servedEntry] this status' entry from the served map
 * @param {string} [props.label] manual override of the visible label
 */
function IssueStatusPill({ status, servedEntry, label }) {
    const entry = getIssueStatus(status, servedEntry);
    const finalLabel = label || entry.label;

    // A value this build does not know goes in the ACCESSIBLE NAME, not only in
    // the tooltip: the tooltip renders on hover into a portal, so a screen
    // reader user — and anyone reading a bug report — would otherwise never see
    // which status the board actually reported.
    //
    // Note this is the mapped key, never Jira's own status name: the service
    // deliberately does not serve `status_name`, so no raw tracker field can
    // reach the DOM.
    const accessibleName =
        entry.recognised || !status
            ? `Status: ${finalLabel}`
            : `Status: ${finalLabel} (unrecognised value "${status}")`;

    return (
        <Tooltip title={entry.description} placement="top">
            <Text
                as={TextAs.SPAN}
                size={TextSizes.EXTRA_SMALL}
                role="status"
                data-tone={entry.tone}
                className={`${styles.tag} ${styles[`tone_${entry.tone}`]}`}
                aria-label={accessibleName}
            >
                {finalLabel}
            </Text>
        </Tooltip>
    );
}

export default IssueStatusPill;
